import {useState, useEffect} from "react"
import {useParams} from "react-router-dom"
import axios from "axios"
import {API_ECP_PROJECTS} from "../../constants/apiUrls"
import useEcpProject from "../../swrCustomHooks/useEcpProject"
import useTries from "../../swrCustomHooks/useTries"
import EcpWorkflowHeader from "../../sections/ecp-workflow-header/EcpWorkflowHeader"
import EcpWorkflowContent from "../../sections/ecp-workflow-content/EcpWorkflowContent"
import LoadingComponent from "../../components/loading-component/LoadingComponent"
import ConfirmationModal from "../../components/confirmation-modal/ConfirmationModal"
import {useNavigate} from "react-router-dom"
import {toast} from "react-toastify"
import {useTranslation} from "react-i18next"
import isUpdateWorkflowNavigation from "../../tools/isUpdateWorkflowNavigation"
import RequireEcpLicence from "../../tools/RequireEcpLicence"
import {useSessionContext} from "../../reactContext/contextCustomHooks/useSessionContext"
import {useTeamContext} from "../../reactContext/contextCustomHooks/useTeamContext"
import {useEcpProjectsContext} from "../../reactContext/contextCustomHooks/useEcpProjectsContext"
import {useEcpWorkflowContext} from "../../reactContext/contextCustomHooks/useEcpWorkflowContext"
import GlossaryModal from "../../sections/glossary-modal/GlossaryModal"

export default function EcpWorkflow() {
    const {t} = useTranslation()
    const {sessionContext} = useSessionContext()
    const {teamContext} = useTeamContext()
    const {ecpProjectsContext, setEcpProjectsAction} = useEcpProjectsContext()
    const {ecpWorkflowContext} = useEcpWorkflowContext()
    const params = useParams()
    const {ecpProject, mutateEcpProject, isLoadingEcpProject, isErrorEcpProject} = useEcpProject(params.projectID)
    const {tries, mutateTries, isLoadingTries, isErrorTries} = useTries(params.projectID)
    const [workflowStep, setWorkflowStep] = useState()
    const [workflowNavigation, setWorkflowNavigation] = useState(-1)
    const [maxStep, setMaxStep] = useState(4)
    const [isPulling, setIsPulling] = useState(ecpProject?.ongoing_process)
    let navigate = useNavigate()
    let intervalID
    const headersRequest = {headers: {Authorization: `Bearer ${sessionContext.idToken}`}}

    const pullingTries = () => {
        axios.get(`${API_ECP_PROJECTS}/${params.projectID}/tries`, headersRequest)
        .then((response) => {
            const trie = response.data[0]
            if (trie.status_code === "done" || trie.status_code === "failed") {
                if (trie.status_code === "done" && isUpdateWorkflowNavigation(trie.step)) {
                    setWorkflowNavigation(workflowNavigation + 1)
                }
                mutateEcpProject()
                setIsPulling(false)
                clearInterval(intervalID)
                intervalID = null
            }
        })
        .catch((error) => {
            console.error("GET /projects/project/tries: ", error)
        })
    }

    const generateEcpProject = (value) => {
        return new Promise((resolve, reject) => {
            if (value) {
                axios.post(`${API_ECP_PROJECTS}/${ecpProjectsContext.projectID}/tries`, {step: "generate_all_ecp"}, headersRequest)
                .then(() => {
                    setIsPulling(true)
                    resolve()
                })
                .catch((error) => {
                    console.error(`POST /projects/${ecpProjectsContext.projectID}/tries:`, error)
                    toast.error(t("An error has occurred"))
                    reject()
                })
            } else {
                resolve()
            }
        })
    }

    const finalizeEcpProject = (value) => {
        return new Promise((resolve, reject) => {
            if (value) {
                axios.post(`${API_ECP_PROJECTS}/${ecpProjectsContext.projectID}/tries`, {step: "finalize"}, headersRequest)
                .then(() => {
                    setIsPulling(true)
                    resolve()
                })
                .catch((error) => {
                    console.error(`POST /projects/${ecpProjectsContext.projectID}/tries: {step: "finalize"}`, error)
                    toast.error(t("An error has occurred"))
                    reject()
                })
            } else {
                resolve()
            }
        })
    }

    const renderConfirmationModal = () => {
        if (ecpWorkflowContext.isConfirmationModal.modal && ecpWorkflowContext.isConfirmationModal.id === ecpProjectsContext.projectID) {
            switch (ecpWorkflowContext.isConfirmationModal.type) {
                case "ecp-generate":
                    return <ConfirmationModal type={"ecp-generate"} generateEcpProject={generateEcpProject} modalMessage={`Your transcript will no longer be editable. Would you like to continue?`}/>
                case "ecp-export":
                    return <ConfirmationModal type={"ecp-export"} finalizeEcpProject={finalizeEcpProject} modalMessage={`Your transcript will no longer be editable. Would you like to continue?`}/>
                default:
                    return null
                }
        }
    };

    useEffect(() => {
        sessionContext.role === "admin" && navigate("/neurowide-dashboard")
        setEcpProjectsAction({type: "SET_ECP_PROJECT_ID", payload: {projectID: params.projectID}})
        mutateTries()
        if (isPulling) {
            window.scrollTo(0, 0)
            intervalID = setInterval(pullingTries, 10000)
        }
    }, [workflowStep, isPulling])

    if (isErrorEcpProject || isErrorTries || isLoadingEcpProject || isLoadingTries || ecpProject.message || tries.message) return <LoadingComponent/>
    return (
        <>
        <RequireEcpLicence types={teamContext.licenceTypes} redirect={true}>
            <div className="VIEW WORKFLOW">
                {renderConfirmationModal()}
                {ecpWorkflowContext.isGlossaryModal.modal && <GlossaryModal type={ecpWorkflowContext.isGlossaryModal.type}/>}
                <EcpWorkflowHeader project={ecpProject} step={workflowStep} workflowNavigation={workflowNavigation} setWorkflowNavigation={setWorkflowNavigation} maxStep={maxStep}/>
                <EcpWorkflowContent params={{
                    project: ecpProject,
                    mutateEcpProject: mutateEcpProject,
                    tries: tries,
                    workflowNavigation: workflowNavigation,
                    setWorkflowStep: setWorkflowStep,
                    setWorkflowNavigation: setWorkflowNavigation,
                    setMaxStep: setMaxStep,
                    isPulling: isPulling,
                    setIsPulling: setIsPulling,
                }}/>
            </div>
        </RequireEcpLicence>
        </>
    )
}